import { getSupabaseBrowserClient } from '../lib/supabase'

type RpcError = { message: string }
type RpcResult = { data: unknown; error: RpcError | null }
type RpcClient = {
  rpc: (name: string, args: Record<string, unknown>) => Promise<RpcResult>
}

type UnknownRecord = Record<string, unknown>

export type LegacyAccessTransitionMember = {
  membershipId: string
  legacyGovernanceManager: boolean
  legacyExecutiveViewer: boolean
}

export type PeopleAccessTransitionReadiness = {
  revision: number
  cutoverReady: boolean
  unresolvedMembershipIds: string[]
  legacyMembers: LegacyAccessTransitionMember[]
}

function record(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as UnknownRecord
    : null
}

function throwSafeReadError(error: RpcError | null, message: string) {
  if (error) {
    throw new Error(message)
  }
}

function parseRevision(value: unknown): number | null {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) return value
  if (typeof value === 'string' && /^\d+$/.test(value)) {
    const parsed = Number(value)
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null
  }
  return null
}

function parseStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    throw new Error('People & access transition readiness returned an invalid response.')
  }

  const result: string[] = []
  for (const entry of value as unknown[]) {
    if (typeof entry !== 'string') {
      throw new Error('People & access transition readiness returned an invalid response.')
    }
    result.push(entry)
  }
  return result
}

function parseLegacyMembers(value: unknown): LegacyAccessTransitionMember[] {
  if (!Array.isArray(value)) {
    throw new Error('People & access transition readiness returned an invalid response.')
  }

  return value.map((entry) => {
    const member = record(entry)
    const capabilities = record(member?.current_capabilities)
    if (!member || typeof member.membership_id !== 'string' || !capabilities) {
      throw new Error('People & access transition readiness returned an invalid response.')
    }

    return {
      membershipId: member.membership_id,
      legacyGovernanceManager: capabilities.legacy_governance_manager === true,
      legacyExecutiveViewer: capabilities.legacy_executive_viewer === true,
    }
  })
}

/**
 * Read the private, revision-bound Release 1 legacy-access preview for the
 * current Organisation Administrator. This is deliberately read-only: the
 * transition apply operation is not called here.
 */
export async function loadPeopleAccessTransitionReadiness(
  organisationId: string,
): Promise<PeopleAccessTransitionReadiness> {
  const client = getSupabaseBrowserClient() as unknown as RpcClient

  const bootstrap = await client.rpc('get_organisation_people_access', {
    p_organisation_id: organisationId,
  })
  throwSafeReadError(bootstrap.error, 'People & access transition readiness could not be loaded.')

  const bootstrapRecord = record(bootstrap.data)
  const revision = parseRevision(bootstrapRecord?.legacyAccessTransitionRevision)
  if (!revision) {
    // Standard members and legacy-only Governance Managers intentionally do not
    // receive this private field. Fail closed rather than treating absence as ready.
    throw new Error('People & access transition readiness is not available for this account.')
  }

  const preview = await client.rpc('preview_r1_legacy_access_transition', {
    p_organisation_id: organisationId,
    p_expected_revision: revision,
    p_mapping: {},
  })
  throwSafeReadError(preview.error, 'People & access transition readiness changed. Retry to refresh it.')

  const previewRecord = record(preview.data)
  const previewRevision = parseRevision(previewRecord?.revision)
  const cutoverReady = previewRecord?.cutover_ready
  if (
    !previewRecord
    || previewRecord.organisation_id !== organisationId
    || previewRevision !== revision
    || typeof cutoverReady !== 'boolean'
  ) {
    throw new Error('People & access transition readiness returned an invalid response.')
  }

  return {
    revision,
    cutoverReady,
    unresolvedMembershipIds: parseStringArray(previewRecord.unresolved_membership_ids),
    legacyMembers: parseLegacyMembers(previewRecord.members),
  }
}
